import { gsap } from "gsap";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";

gsap.registerPlugin(MotionPathPlugin);

function wrap01(x) {
  const y = x % 1;
  return y < 0 ? y + 1 : y;
}

/**
 * preserveAspectRatio="xMidYMid meet" と同じ等倍率で、パスの実長（px）を推定する。
 */
function pathLengthPx(svg, pathSelector) {
  const vb = svg.viewBox.baseVal;
  const vbW = vb.width || 2884;
  const vbH = vb.height || 245;
  const lenSvg = MotionPathPlugin.getLength(pathSelector);
  const rect = svg.getBoundingClientRect();
  const sx = vbW > 0 ? rect.width / vbW : 0;
  const sy = vbH > 0 ? rect.height / vbH : 0;
  const scale =
    sx > 0 && sy > 0 ? Math.min(sx, sy) : Math.max(sx, sy) || sx || 1;
  return lenSvg * scale;
}

/**
 * 各文字の見た目の幅を取り、隣同士に必要なパス進行度の差分を積算する。
 * 固定 step より、繰り返し境界や欧文+和文の混在で前と被りにくい。
 *
 * @param {HTMLElement} root
 * @param {string} pathSelector
 * @param {Element[]} letterEls
 * @param {gsap.core.Tween[]} tweens
 * @param {number} spacingMult
 * @returns {Float64Array} cumulative[i] = 文字0から i までの弧上オフセット（progress 換算・非正規化）
 */
function buildCumulativeOffsets(
  root,
  pathSelector,
  letterEls,
  tweens,
  spacingMult,
) {
  const n = letterEls.length;
  const out = new Float64Array(n);
  if (n < 2) {
    return out;
  }

  const svg = root.querySelector(".p-top-about__wave__svg");
  if (!svg) {
    return out;
  }

  const pathLenPx = pathLengthPx(svg, pathSelector);
  if (!pathLenPx || pathLenPx < 8) {
    return out;
  }

  const fsPx = parseFloat(getComputedStyle(letterEls[0]).fontSize) || 16;
  const minGap = (fsPx * 0.42 * spacingMult) / pathLenPx;

  // 測定時はパス上にばらして AABB が重ならないようにする
  for (let i = 0; i < n; i++) {
    tweens[i].progress((i + 0.5) / n);
  }
  void letterEls[0].offsetWidth;

  const widths = letterEls.map((el) => {
    const r = el.getBoundingClientRect();
    return Math.max(r.width, fsPx * 0.55);
  });

  let acc = 0;
  out[0] = 0;
  for (let i = 1; i < n; i++) {
    const pair = (Math.max(widths[i - 1], widths[i]) * spacingMult) / pathLenPx;
    acc += Math.max(pair, minGap, 0.018);
    out[i] = acc;
  }

  // 末尾→先頭の継ぎ目にも十分な隙間を確保して、1周目/2周目の重なりを防ぐ
  const last = out[n - 1];
  if (last > 0) {
    const avgGap = last / Math.max(1, n - 1);
    const seamGap = Math.max(minGap, avgGap, 0.018);
    const maxSpan = Math.max(0.7, 1 - seamGap);
    if (last > maxSpan) {
      const scale = maxSpan / last;
      for (let i = 1; i < n; i++) {
        out[i] *= scale;
      }
    }
  }

  return out;
}

/**
 * @param {HTMLElement} root
 */
export function initWaveMotionTicker(root) {
  const pathSelector = root.dataset.wavePath || "#p-top-about-wave-path";
  const pathEl = document.querySelector(pathSelector);
  const track = root.querySelector(".p-top-about__wave__track");
  const raw = root.dataset.waveTicker;

  if (!pathEl || !track || !raw) {
    return;
  }

  const rotateOffset = parseFloat(root.dataset.waveRotateOffset);
  const autoRotate = Number.isFinite(rotateOffset) ? rotateOffset : 180;

  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    track.textContent = raw;
    track.classList.add("is-static");
    return;
  }

  const duration = Math.max(4, parseFloat(root.dataset.waveDuration) || 28);
  const repeat = Math.max(2, parseInt(root.dataset.waveRepeat, 10) || 5);
  const phrase = raw.repeat(repeat);
  const chars = [...phrase];

  const rawMult = parseFloat(root.dataset.waveSpacingMult);
  const spacingMult =
    Number.isFinite(rawMult) && rawMult > 0 ? rawMult : 1.32;

  track.textContent = "";
  const frag = document.createDocumentFragment();
  chars.forEach((ch) => {
    const span = document.createElement("span");
    span.className = "p-top-about__wave__char";
    span.setAttribute("aria-hidden", "true");
    span.textContent = ch === " " ? "\u00a0" : ch;
    frag.appendChild(span);
  });
  track.appendChild(frag);

  const letters = track.querySelectorAll(".p-top-about__wave__char");
  const letterEls = [...letters];
  const tweens = letterEls.map((el) =>
    gsap.to(el, {
      motionPath: {
        path: pathSelector,
        align: pathSelector,
        alignOrigin: [0.5, 0.5],
        autoRotate,
      },
      duration: 1,
      ease: "none",
      paused: true,
    }),
  );

  let cumulative = new Float64Array(letterEls.length);

  const applyLayout = () => {
    cumulative = buildCumulativeOffsets(
      root,
      pathSelector,
      letterEls,
      tweens,
      spacingMult,
    );
  };

  const state = { t: 0 };
  let mainTween;

  const start = () => {
    applyLayout();
    if (mainTween) {
      mainTween.kill();
    }
    mainTween = gsap.to(state, {
      t: 1,
      duration,
      repeat: -1,
      ease: "none",
      onUpdate: () => {
        tweens.forEach((tw, i) => {
          const u = wrap01(state.t + cumulative[i]);
          tw.progress(1 - u);
        });
      },
    });
  };

  const scheduleStart = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(start);
    });
  };

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(scheduleStart).catch(scheduleStart);
  } else {
    scheduleStart();
  }

  if (typeof ResizeObserver !== "undefined") {
    const ro = new ResizeObserver(() => {
      applyLayout();
    });
    ro.observe(root);
  }
}
